/**
 * Ouvrir une demande sur un formulaire public.
 *
 * Passe par une route HTTP du serveur plutôt que par une écriture directe,
 * pour une seule raison : une écriture Convex ne dit pas d'où elle vient, donc
 * le serveur ne peut pas compter les demandes par appareil. Il en était réduit
 * à compter par formulaire, donc à pouvoir le fermer, donc à offrir à un
 * attaquant de quoi couper la porte d'entrée commerciale du cabinet. La route,
 * elle, voit l'origine, et ne ralentit que celle qui abuse.
 *
 * L'ancien chemin reste en réserve : tant qu'un environnement n'a pas son
 * adresse de routes configurée, il continue de marcher comme avant. Fermer
 * d'abord et configurer ensuite laisserait un formulaire mort entre les deux.
 */

export type OuvertureRefusee = { trop: true } | { testHumain: true };

/**
 * L'adresse des routes HTTP du serveur.
 *
 * Convex sert les écritures et les routes sur deux domaines différents. En
 * nuage, le second se déduit du premier. En auto-hébergé, ce sont deux noms
 * sans rapport (`api-convex-prod` et `site-convex-prod`) : il faut alors
 * `VITE_CONVEX_SITE_URL`, sinon on renvoie null et l'ancien chemin sert.
 */
export function adresseDesRoutes(): string | null {
  const explicite = import.meta.env.VITE_CONVEX_SITE_URL as string | undefined;
  if (explicite) return explicite.replace(/\/+$/, "");

  const convex = import.meta.env.VITE_CONVEX_URL as string | undefined;
  if (convex?.includes(".convex.cloud")) {
    return convex.replace(".convex.cloud", ".convex.site").replace(/\/+$/, "");
  }
  return null;
}

/**
 * Renvoie l'identifiant de la demande ouverte, `{ trop: true }` si cet appareil
 * en a trop ouvert, ou null s'il faut se rabattre sur l'ancien chemin.
 *
 * Une panne de réseau rend null plutôt que de lever : le repli doit pouvoir
 * jouer. Un refus franc du serveur, lui, est un refus, et se propage.
 */
export async function ouvrirDemandePublique(params: {
  publicFormId: string;
  preferredLanguage?: string;
  device?: string;
  /** Le texte de consentement tel qu'il était à l'écran, mot pour mot. */
  consentementTexte?: string;
  /** Jeton du test d'humanité. Absent tant que le test est éteint. */
  jetonHumain?: string;
}): Promise<{ submissionId: string } | OuvertureRefusee | null> {
  const base = adresseDesRoutes();
  if (!base) return null;

  let reponse: Response;
  try {
    reponse = await fetch(`${base}/formulaire-public/demarrer`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(params),
    });
  } catch {
    return null;
  }

  if (reponse.status === 429) return { trop: true };
  // Un refus du test d'humanité est un refus, pas une panne : se rabattre sur
  // l'ancien chemin le contournerait, ce qui viderait la mesure de son sens.
  if (reponse.status === 403) return { testHumain: true };
  if (!reponse.ok) return null;

  const corps = (await reponse.json().catch(() => null)) as
    | { submissionId?: string }
    | null;
  return corps?.submissionId ? { submissionId: corps.submissionId } : null;
}
